import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Phone, Truck, ShieldCheck, Zap, Headphones } from "lucide-react";
import { fetchProducts } from "../lib/api";
import { useDocumentMeta } from "../hooks/use-document-meta";
import { useJsonLd } from "../hooks/use-json-ld";
import { ProductCard } from "../components/ProductCard";
import { CatalogRequestCTA } from "../components/v2/CatalogRequestCTA";
import { VideoBand } from "../components/VideoBand";

// Load every concept JSON at build time. require.context is the CRA/Webpack 5
// native primitive — NOT import.meta.glob, which is Vite-only. Inactive
// concepts (active: false) are filtered out so the page returns the
// "Concept introuvable" view if a slug is hidden via the future CMS.
const conceptCtx = require.context("../content/concepts", false, /\.json$/);
const CONCEPTS = conceptCtx
  .keys()
  .map((k) => conceptCtx(k))
  .filter((c) => c.active !== false);
const CONCEPT_BY_SLUG = Object.fromEntries(CONCEPTS.map((c) => [c.slug, c]));

const DEFAULT_ESSENTIELS_TITLE = "La sélection essentielle pour votre activité.";

const REASSURANCE = [
  { icon: Truck, title: "Livraison pro", text: "Livraison professionnelle en Île-de-France, adaptée aux fréquences de commande des fast-foods." },
  { icon: ShieldCheck, title: "Sélection grossiste", text: "Chaque référence est validée pour la régularité, la qualité et le rendement en service." },
  { icon: Zap, title: "Service rapide", text: "Produits calibrés pour les rushes et les volumes, sans casser la chaîne de production." },
  { icon: Headphones, title: "Accompagnement commercial", text: "Un interlocuteur dédié vous oriente sur les références adaptées à votre carte et votre activité." },
];

export default function Sector() {
  const { slug } = useParams();
  const cfg = CONCEPT_BY_SLUG[slug];
  const [products, setProducts] = useState([]);

  useDocumentMeta({
    title: cfg
      ? `${cfg.label || slug} — Fournisseur grossiste alimentaire B2B restauration rapide`
      : "Concept introuvable",
    description: cfg
      ? cfg.seoDescription ||
        `${cfg.label || slug} : sélection produits, calibrage et emballages adaptés. Grossiste alimentaire B2B et fournisseur restauration rapide en Île-de-France. Devis sous 24h.`
      : null,
    canonicalPath: cfg ? `/concept/${slug}` : null,
    robots: cfg ? "index" : "noindex",
  });
  useJsonLd(`breadcrumb:concept:${slug}`, cfg ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://www.fritesdorees.fr/" },
      { "@type": "ListItem", "position": 2, "name": "Concepts", "item": "https://www.fritesdorees.fr/concept/" },
      { "@type": "ListItem", "position": 3, "name": cfg.label || slug, "item": `https://www.fritesdorees.fr/concept/${slug}` },
    ],
  } : null);
  // ItemList JSON-LD: enumerates the 6 essentials curated for this concept.
  // Built from the resolved `products` state so we only emit valid items
  // (skips any productSlug that didn't resolve to a fiche). Skipped on the
  // soft-404 "Concept introuvable" view.
  useJsonLd(
    `itemlist:concept:${slug}`,
    cfg && Array.isArray(products) && products.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: `${cfg.label || slug} — Sélection essentiels grossiste Frites Dorées`,
          numberOfItems: products.length,
          itemListElement: products.slice(0, 12).map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://www.fritesdorees.fr/produit/${p.slug}`,
            name: p.name,
          })),
        }
      : null
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!cfg) return;
    const slugs = cfg.productSlugs || [];
    if (slugs.length === 0) {
      setProducts([]);
      return;
    }
    Promise.all(
      slugs.map((s) => fetchProducts({ search: "", limit: 500 }).then((all) => all.find((p) => p.slug === s)))
    ).then((r) => setProducts(r.filter(Boolean)));
  }, [slug, cfg]);

  if (!cfg) {
    return (
      <main className="min-h-screen pt-40 pb-24 px-6 text-center bg-[#050505]" data-testid="sector-not-found">
        <div className="max-w-xl mx-auto">
          <p className="tag-label mb-5 flex items-center justify-center gap-3">
            <span className="w-10 h-px bg-[#FFC300]"></span>
            <span>Concept introuvable</span>
            <span className="w-10 h-px bg-[#FFC300]"></span>
          </p>
          <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tighter mb-4">
            Cette page n'existe pas.
          </h1>
          <p className="font-body text-zinc-400 mb-8 leading-relaxed">
            Nos concepts disponibles : kebab, burger, tacos. Vous pouvez explorer tout le catalogue ou nous décrire votre activité.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/catalogue" className="btn-primary inline-flex items-center justify-center gap-2">
              Voir le catalogue <ArrowUpRight size={16} />
            </Link>
            <Link to="/contact" className="btn-secondary inline-flex items-center justify-center gap-2">
              Parler à un commercial
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const bullets = cfg.bullets || [];
  const productSlugs = cfg.productSlugs || [];
  const essentialsTitle = cfg.essentialsTitle || DEFAULT_ESSENTIELS_TITLE;
  const label = cfg.label || slug;

  return (
    <main data-testid={`sector-page-${slug}`} className="bg-[#050505]">
      {/* Hero */}
      <section className="relative pt-36 lg:pt-40 pb-20 lg:pb-24 px-6 md:px-10 lg:px-14 overflow-hidden border-b border-white/5">
        <div className="absolute -top-20 -right-20 w-[600px] h-[600px] bg-[#FFC300] opacity-[0.05] blur-3xl rounded-full pointer-events-none"></div>
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="tag-label mb-5 flex flex-wrap items-center gap-3"
            >
              <span className="w-10 h-px bg-[#FFC300]"></span>
              <span>{cfg.kicker}</span>
              <span className="text-white/30">·</span>
              <span className="text-white/70">{productSlugs.length} essentiels sélectionnés</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="font-display text-5xl sm:text-7xl lg:text-[8rem] uppercase tracking-tighter leading-[0.85]"
              data-testid="sector-title"
            >
              {cfg.title}
              {cfg.title_highlight && (
                <>
                  {" "}
                  <br />
                  <span className="text-[#FFC300]">{cfg.title_highlight}</span>
                </>
              )}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.25 }}
              className="mt-8 max-w-lg text-xl text-zinc-300 font-body leading-relaxed"
            >
              {cfg.promise}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.4 }}
              className="mt-10 flex flex-col sm:flex-row flex-wrap gap-3"
            >
              <Link
                to={`/contact?interest=${slug}`}
                className="btn-primary btn-glow"
                data-testid="sector-cta-quote"
              >
                Demander une sélection <ArrowUpRight size={18} />
              </Link>
              <a href="#essentiels" className="btn-secondary" data-testid="sector-cta-products">
                Voir les produits
              </a>
              <a
                href="tel:+33148680303"
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="sector-cta-phone"
              >
                <Phone size={16} /> 01 48 68 03 03
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 relative aspect-square fd-stage overflow-hidden"
          >
            {cfg.hero && (
              <img
                src={cfg.hero}
                alt={label}
                // Above-the-fold LCP image on /concept/:slug — eager load +
                // high fetch priority improves LCP on the concept landing pages.
                fetchpriority="high"
                decoding="async"
                className="absolute inset-0 w-full h-full object-contain p-10"
               
              />
            )}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,195,0,0.2),_transparent_65%)] pointer-events-none"></div>
          </motion.div>
        </div>
      </section>

      {/* Optional editorial block — rendered only if the concept JSON declares
          an `editorial` field. Lets a concept page argue *why* its category
          structures a profitable fast-food setup before jumping into the
          product grid. Backward-compatible: existing concepts (kebab, burger,
          tacos) have no `editorial` field and skip this section entirely. */}
      {cfg.editorial && Array.isArray(cfg.editorial.cards) && cfg.editorial.cards.length > 0 && (
        <section
          data-testid={`sector-editorial-${slug}`}
          className="relative px-6 md:px-10 lg:px-14 py-20 lg:py-28 border-b border-white/5 bg-fd-elevated overflow-hidden"
        >
          <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-[#FFC300] opacity-[0.04] blur-3xl rounded-full pointer-events-none"></div>
          <div className="relative max-w-[1400px] mx-auto">
            {cfg.editorial.kicker && (
              <div className="tag-label mb-5 flex items-center gap-3">
                <span className="w-10 h-px bg-[#FFC300]"></span>
                <span>{cfg.editorial.kicker}</span>
              </div>
            )}
            {(cfg.editorial.title || cfg.editorial.title_highlight) && (
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="font-display text-4xl md:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.95] max-w-3xl"
              >
                {cfg.editorial.title}
                {cfg.editorial.title_highlight && (
                  <>
                    {" "}
                    <span className="text-[#FFC300]">{cfg.editorial.title_highlight}</span>
                  </>
                )}
              </motion.h2>
            )}
            {cfg.editorial.subtitle && (
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="mt-6 max-w-2xl font-body text-zinc-300 text-base md:text-lg leading-relaxed"
              >
                {cfg.editorial.subtitle}
              </motion.p>
            )}
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {cfg.editorial.cards.map((card, i) => (
                <motion.div
                  key={card.title || i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: 0.1 + i * 0.08 }}
                  className="border border-white/10 p-5 md:p-6 bg-[#0d0d0d] hover:border-[#FFC300]/40 transition-colors"
                >
                  <p className="mono-label-gold mb-3">{String(i + 1).padStart(2, "0")}</p>
                  <p className="font-display text-base md:text-lg uppercase tracking-tight mb-3">{card.title}</p>
                  <p className="font-body text-sm text-zinc-400 leading-relaxed">{card.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Cinematic appetite break: per-concept food video.
          Falls back to catalog-frying.mp4 (the only universally-on-topic asset
          available — the previous hardcoded "sector-burger.mp4" did not exist
          on disk, the request 404'd silently and only the poster survived). */}
      <VideoBand
        videoSrc={cfg.videoSrc || "catalog-frying.mp4"}
        posterSrc={cfg.videoPosterSrc || "catalog-frying-poster.jpg"}
        eyebrow={`Concept ${label} · L'expérience`}
        title={<>L'appétit <br /> au <span className="text-[#FFC300]">premier regard.</span></>}
        tagline={`Tout ce qu'il faut pour que votre ${label.toLowerCase()} soit mémorable dès la première bouchée. On vous équipe, vous servez.`}
        height="h-[60vh]"
        testId={`sector-video-band-${slug}`}
      />

      {/* Essentiels */}
      <section id="essentiels" className="px-6 md:px-10 lg:px-14 py-20 lg:py-28 border-b border-white/5 scroll-mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="tag-label mb-5 flex items-center gap-3">
              <span className="w-10 h-px bg-[#FFC300]"></span>
              <span>Les essentiels pour votre activité</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tighter leading-[0.95]" data-testid="sector-essentiels-title">
              {essentialsTitle}
            </h2>
            <ul className="mt-10 space-y-3">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 font-body text-zinc-300">
                  <Check size={16} className="text-[#FFC300] mt-1.5 shrink-0" /> <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-8">
            {productSlugs.length === 0 ? (
              <div className="text-zinc-400 font-body py-20 text-center">Sélection à venir.</div>
            ) : products.length === 0 ? (
              <div className="text-zinc-400 font-body py-20 text-center">Chargement de la sélection...</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Réassurance B2B */}
      <section className="px-6 md:px-10 lg:px-14 py-16 lg:py-24 border-b border-white/5" data-testid="sector-reassurance">
        <div className="mb-10">
          <div className="tag-label mb-4 flex items-center gap-3">
            <span className="w-10 h-px bg-[#FFC300]"></span>
            <span>Ce qui change avec un grossiste</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tighter leading-[0.95] max-w-2xl">
            Une logistique pro. <span className="text-[#FFC300]">Une équipe à l'écoute.</span>
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {REASSURANCE.map(({ icon: Icon, title, text }) => (
            <div key={title} className="border border-white/10 p-5 md:p-6 bg-[#0d0d0d] hover:border-[#FFC300]/40 transition-colors">
              <div className="w-10 h-10 mb-4 border border-white/15 flex items-center justify-center">
                <Icon size={16} className="text-[#FFC300]" />
              </div>
              <p className="font-display text-base md:text-lg uppercase tracking-tight mb-2">{title}</p>
              <p className="font-body text-sm text-zinc-400 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Catalogue lead-gen — same component as Home/Catalog so the prospect
          always has a "Demander le catalogue" path on every concept page. */}
      <CatalogRequestCTA
        testId={`sector-catalog-cta-${slug}`}
        eyebrow={`Catalogue ${cfg.label || slug}`}
        headline="Feuilletez notre catalogue grossiste en ligne."
        tagline={`Toutes les références ${cfg.label ? cfg.label.toLowerCase() : ""} avec poids, colisage et palettisation. Pour un devis, un conseiller vous répond sous 24h ouvrées.`}
      />

      {/* Final CTA — tailored-selection ask, complementary to the catalogue block. */}
      <section className="bg-[#FFC300] text-black py-20 lg:py-28 px-6 md:px-10 lg:px-14">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tighter leading-[0.95] max-w-2xl">
            Vous voulez une sélection adaptée à votre carte ?
          </h2>
          <Link
            to={`/contact?interest=${slug}`}
            className="btn-secondary !border-black !text-black hover:!bg-black hover:!text-[#FFC300]"
            data-testid="sector-cta-final"
          >
            Demander ma sélection <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </main>
  );
}
